import { api } from 'store/api';
import { ApiResponse } from 'store/api/types';
import moment from 'moment';
import {
  ActivityLog,
  DataStatus,
  JournalPostData,
  SaveReturnRequestData,
  VatGenerateRequestData,
  VatReportData,
  VatReturnLog,
  VatRunData,
  VatRunRequestData,
  ViewRunRequestData,
} from './models';
import {
  ActivityRoleUpdateRequest,
  VatLoadRequestData,
} from './models/requestData';
import { EmailDataParametersRequest } from './models/emailDataParametersRequest';
import { ActivityRoles } from 'store/app/enums';
import {
  AccountsFilter,
  ExcelFileResponse,
  JournalType,
  MappingCodes,
  ReportPage,
  TaxRatesFilter,
} from './models/vatReportData';
import {
  AllExceptionsDetails,
  ExceptionsWrapper,
} from './models/exceptionWrapper';
import { TransactionAttachment } from './models/transactionAttachments';
import { TOKEN_ID_HEADER } from 'store/app/constants';
import { BoxValue } from './types';
import { getHmrcHeader } from './hmrcUtils';
import { HmrcSubmissionRequest } from './models/hmrcSubmissionRequest';

export interface GetVatLogsResponse extends ApiResponse {
  data: { logs: VatReturnLog[] };
}
export const getVatLogs = (
  connectionId: string,
  mappingId: string
): Promise<GetVatLogsResponse> =>
  api.get<GetVatLogsResponse>(
    `/auth/vat/taxreturn/logs/${connectionId}/mapping/${mappingId}`,
    {
      headers: {
        [TOKEN_ID_HEADER]: connectionId,
      },
    }
  );
export interface GetRunDataResponse extends ApiResponse {
  data: VatRunData;
}

export const getRunData = (
  data: VatRunRequestData
): Promise<GetRunDataResponse> => {
  const url =
    '/auth/vat/run/' +
    data.connectionId +
    '/mapping/' +
    data.mappingId +
    '?fromDate=' +
    data.fromDate +
    '&toDate=' +
    data.toDate +
    '&currency=' +
    data.currency;
  return getHmrcHeader().then((hmrcHeader) =>
    api.post<GetRunDataResponse>(url, {
      body: { hmrcHeader },
      headers: {
        [TOKEN_ID_HEADER]: data.connectionId,
      },
    })
  );
};

export const getViewRunData = (
  data: ViewRunRequestData
): Promise<GetRunDataResponse> => {
  const url =
    '/auth/vat/view/' +
    data.connectionId +
    '/mapping/' +
    data.mappingId +
    '/log/' +
    data.dataLogId;

  return api.get<GetRunDataResponse>(url, {
    headers: {
      [TOKEN_ID_HEADER]: data.connectionId,
    },
  });
};

export interface GenerateReportResponse extends ApiResponse {
  data: VatReportData;
}
export const generateReport = (
  data: VatGenerateRequestData,
  accrualsPage: number,
  cashAccountingPage: number,
  reportId: string
): Promise<GenerateReportResponse> => {
  const dataLogIdParam = data.dataLogId ? `&dataLogId=${data.dataLogId}` : '';
  const url =
    '/auth/vat/generate/' +
    data.connectionId +
    '/mapping/' +
    data.mappingId +
    '?fromDate=' +
    data.fromDate +
    '&toDate=' +
    data.toDate +
    '&currency=' +
    data.currency +
    '&accrualsPage=' +
    accrualsPage +
    '&cashAccountingPage=' +
    cashAccountingPage +
    '&pageSize=15000' +
    '&reportId=' +
    reportId +
    dataLogIdParam;
  return api.get<GenerateReportResponse>(url, {
    headers: {
      [TOKEN_ID_HEADER]: data.connectionId,
    },
  });
};

export interface LoadReportResponse extends ApiResponse {
  data: VatReportData;
}

export const loadReport = (
  data: VatLoadRequestData,
  accrualsPage: number,
  cashAccountingPage: number,
  reportId: string
): Promise<LoadReportResponse> => {
  const url =
    '/auth/vat/view/report/' +
    data.connectionId +
    '/mapping/' +
    data.mappingId +
    '/log/' +
    data.dataLogId +
    '?currency=' +
    data.currency +
    '&accrualsPage=' +
    accrualsPage +
    '&cashAccountingPage=' +
    cashAccountingPage +
    '&pageSize=15000' +
    '&reportId=' +
    reportId;
  return api.get<LoadReportResponse>(url, {
    headers: {
      [TOKEN_ID_HEADER]: data.connectionId,
    },
  });
};

export const loadReportTotals = (
  mappingId: string,
  dataLogId: string
): Promise<LoadReportResponse> => {
  const url = `/auth/vat/totals/${mappingId}/log/${dataLogId}`;
  return api.get<LoadReportResponse>(url);
};

export interface LoadReconResponse extends ApiResponse {
  data: Map<string, string>;
}
export const loadRecon = (
  tokenId: string,
  toDate: string,
  mappingId?: string
): Promise<LoadReconResponse> => {
  let url = `/auth/vat/reconciliation/load/${tokenId}?toDate=${toDate}`;
  if (mappingId) {
    url += `&mappingId=${mappingId}`;
  }
  return api.get<LoadReconResponse>(url, {
    headers: {
      [TOKEN_ID_HEADER]: tokenId,
    },
  });
};

export interface SaveReturnResponse extends ApiResponse {
  data: { dataLogId: string };
}

export const saveReturn = (
  data: SaveReturnRequestData,
  reportId: string
): Promise<SaveReturnResponse> => {
  const url =
    '/auth/status/save/report/' +
    data.connectionId +
    '?mappingId=' +
    data.mappingId +
    '&fromDate=' +
    data.fromDate +
    '&toDate=' +
    data.toDate +
    '&generatedDate=' +
    data.generatedDate +
    '&currency=' +
    data.currency +
    '&reportingTypeId=' +
    data.reportingTypeId +
    '&reportId=' +
    reportId;
  return api.post<SaveReturnResponse>(url, {
    body: {
      jsonTotals: data.jsonTotals,
      totalTaxTransactions: data.totalTaxTransactions,
      totalTaxExceptions: data.totalTaxExceptions,
      transactionsCount: data.transactionsCount,
      exceptionsCount: data.exceptionsCount,
      hmrcPeriodKey: data.hmrcPeriodKey,
    },
    headers: {
      [TOKEN_ID_HEADER]: data.connectionId,
    },
  });
};

export const updateReturn = (
  data: SaveReturnRequestData,
  reportId: string
): Promise<ApiResponse> => {
  const url =
    '/auth/status/update/report/' +
    data.dataLogId +
    '?tokenId=' +
    data.connectionId +
    '&reportingTypeId=' +
    data.reportingTypeId +
    '&reportId=' +
    reportId;

  return api.post<ApiResponse>(url, {
    body: {
      jsonTotals: data.jsonTotals,
      totalTaxTransactions: data.totalTaxTransactions,
      totalTaxExceptions: data.totalTaxExceptions,
      transactionsCount: data.transactionsCount,
      exceptionsCount: data.exceptionsCount,
    },

    headers: {
      [TOKEN_ID_HEADER]: data.connectionId,
    },
  });
};

export const updateReportFields = (
  dataLogId: string,
  reference: string,
  returnTax: string
): Promise<ApiResponse> => {
  // NOTE: Our API client drops query params when value === ''.
  // This endpoint must support clearing fields, so we build the query string
  // manually to ensure `reference=` / `returnTax=` are still sent.
  const baseUrl = `/auth/status/update/report/${dataLogId}/fields`;
  const qs = `reference=${encodeURIComponent(
    reference ?? ''
  )}&returnTax=${encodeURIComponent(returnTax ?? '')}`;
  const url = `${baseUrl}?${qs}`;

  return api.patch<ApiResponse>(url);
};

export const cancelReturn = (
  dataLogId: string,
  mappingId: string
): Promise<ApiResponse> => {
  const url = `/auth/status/cancel/report/${dataLogId}?mappingId=${mappingId}`;

  return api.post<ApiResponse>(url);
};

export const updateActivityRole = (
  data: ActivityRoleUpdateRequest
): Promise<ApiResponse> => {
  const removing =
    data.activityRole === ActivityRoles.REMOVE_CLIENT ||
    data.activityRole === ActivityRoles.REMOVE_REVIEWER;
  const action = removing ? 'remove' : 'assign';
  const actRole = removing
    ? data.activityRole.replace('remove-', '')
    : data.activityRole;
  const mapping = removing ? '' : `/mapping/${data.mappingId}`;
  const url =
    '/auth/activity/' +
    action +
    '/' +
    actRole +
    '/' +
    data.memberUser?.accountUuid +
    '/datalog/' +
    data.dataLogId +
    mapping;

  return api.post<ApiResponse>(url);
};

export const addNote = (
  note: string,
  dataLogId: string
): Promise<ApiResponse> => {
  const url = `/auth/activity/add/note/${dataLogId}`;

  return api.post<ApiResponse>(url, { body: { note } });
};

export interface GetActivityLogsResponse extends ApiResponse {
  data: { activities: ActivityLog[] };
}

export const getActivityLogs = (
  dataLogId: string
): Promise<GetActivityLogsResponse> => {
  const url = `/auth/activity/load/activity/datalog/${dataLogId}`;

  return api.get<GetActivityLogsResponse>(url);
};

export const awaitingApproval = (
  dataLogId: string,
  reportUrl: string,
  mappingId: string
): Promise<ApiResponse> => {
  const url = `/auth/status/awaiting-approval/report/${dataLogId}?mappingId=${mappingId}`;

  return api.post<ApiResponse>(url, { body: { reportUrl } });
};

export const internallyApprove = (
  dataLogId: string,
  reportUrl: string,
  mappingId: string
): Promise<ApiResponse> => {
  const url = `/auth/status/internal-approved/report/${dataLogId}?mappingId=${mappingId}`;

  return api.post<ApiResponse>(url, { body: { reportUrl } });
};

export const sendClientApproval = (
  dataLogId: string,
  emailData: EmailDataParametersRequest,
  mappingId: string
): Promise<ApiResponse> => {
  const url = `/auth/status/client-approval/report/${dataLogId}?mappingId=${mappingId}`;

  return api.post<ApiResponse>(url, { body: { emailData } });
};

export const clientApprove = (
  dataLogId: string,
  reportUrl: string
): Promise<ApiResponse> => {
  const url = `/auth/status/client-approved/report/${dataLogId}`;

  return api.post<ApiResponse>(url, { body: { reportUrl } });
};

export const notApprove = (
  dataLogId: string,
  emailData: EmailDataParametersRequest,
  mappingId: string
): Promise<ApiResponse> => {
  const url = `/auth/status/revert/report/${dataLogId}?mappingId=${mappingId}`;

  return api.post<ApiResponse>(url, { body: { emailData } });
};

export const submitReturn = (
  dataLogId: string,
  mappingId: string
): Promise<ApiResponse> => {
  const subDate = moment().startOf('day').format('DD/MM/YYYY HH:mm:ss');
  const url = `/auth/status/submit/report/${dataLogId}/mapping/${mappingId}?submittedDate=${subDate}`;

  return api.post<ApiResponse>(url);
};

export const submitHmrcReturn = (
  dataLogId: string,
  mappingId: string,
  hmrcSubmissionRequest: HmrcSubmissionRequest
): Promise<ApiResponse> => {
  const submittedDate = moment().startOf('day').format('DD/MM/YYYY HH:mm:ss');
  const url = `/auth/status/submit/report/hmrc/${dataLogId}/mapping/${mappingId}/post`;

  return getHmrcHeader().then((hmrcHeader) =>
    api.post<ApiResponse>(url, {
      body: {
        submissionData: { ...hmrcSubmissionRequest, submittedDate },
        clientInfo: { hmrcHeader },
      },
    })
  );
};

export const lockReturn = (
  dataLogId: string,
  mappingId: string
): Promise<ApiResponse> => {
  const url = `/auth/status/lock/report/${dataLogId}?mappingId=${mappingId}`;

  return api.post<ApiResponse>(url);
};

export interface CheckEntityStatusResponse extends ApiResponse {
  data: { status: DataStatus };
}

export const checkEntityStatus = (
  connectionId: string
): Promise<CheckEntityStatusResponse> =>
  api.get<CheckEntityStatusResponse>(
    `/auth/organisation/get/data/download/status/${connectionId}`,
    {
      headers: {
        [TOKEN_ID_HEADER]: connectionId,
      },
    }
  );

export interface ExportedExcelFileResponse extends ApiResponse {
  data: ExcelFileResponse;
}

export const getExcelFileForExport = (
  reportTypeId: string
): Promise<ExportedExcelFileResponse> =>
  api.get<ExportedExcelFileResponse>(`/file/report/${reportTypeId}`);

export const exceptionsAction = (
  dataLogId: string,
  mappingId: string,
  exceptionsWrapper: ExceptionsWrapper[],
  allExceptionsDetails: AllExceptionsDetails | null,
  reportId: string
): Promise<ExportedExcelFileResponse> =>
  api.post<ExportedExcelFileResponse>(
    `/auth/exceptions/action/${dataLogId}/mapping/${mappingId}`,
    {
      body: {
        exceptionsActionList: exceptionsWrapper,
        allExceptionsDetails,
        reportId,
      },
    }
  );

export interface ManualJournalPostResponse extends ApiResponse {
  data: { manualJournalId?: string; manualJournalUrl?: string };
}
export const manualJournalPost = (
  dataLogId: string,
  mappingId: string,
  journalPostData: JournalPostData
): Promise<ManualJournalPostResponse> => {
  const url = `/auth/status/submit/report/${dataLogId}/mapping/${mappingId}/post`;

  return api.post<ManualJournalPostResponse>(url, { body: journalPostData });
};

export interface ReportPageResponse extends ApiResponse {
  data: ReportPage;
}

export const getReportDataPage = (
  reportId: string,
  page: number,
  pageSize: number,
  transactionType: string,
  groupingType: string,
  filters?: object
): Promise<ReportPageResponse> => {
  const url = `/auth/vat/query/${reportId}?page=${page}&pageSize=${pageSize}&transactionType=${transactionType}&groupingType=${groupingType}`;
  const body = filters ? { body: filters } : {};
  return api.post<ReportPageResponse>(url, body);
};
export interface MappingCodesResponse extends ApiResponse {
  data: MappingCodes;
}

export const getMappingCodesFilter = (
  mappingId: string
): Promise<MappingCodesResponse> => {
  const url = `/auth/vat/query/filter/mapping/${mappingId}`;
  return api.get<MappingCodesResponse>(url);
};
export interface AccountsFiltersResponse extends ApiResponse {
  data: AccountsFilter;
}
export const getAccountsFilters = (
  tokenId: string
): Promise<AccountsFiltersResponse> => {
  const url = `/auth/vat/query/filter/accounts/${tokenId}`;
  return api.get<AccountsFiltersResponse>(url, {
    headers: {
      [TOKEN_ID_HEADER]: tokenId,
    },
  });
};

export interface TaxRatesFiltersResponse extends ApiResponse {
  data: TaxRatesFilter;
}

export const getTaxRateFilters = (
  tokenId: string
): Promise<TaxRatesFiltersResponse> => {
  const url = `/auth/vat/query/filter/taxRates/${tokenId}`;
  return api.get<TaxRatesFiltersResponse>(url, {
    headers: {
      [TOKEN_ID_HEADER]: tokenId,
    },
  });
};
export interface TransactionAttachmentsResponse extends ApiResponse {
  data: {
    files?: TransactionAttachment[];
  };
}

export const getTransactionAttachment = (
  tokenId: string,
  transactionId: string,
  transactionUuid: string,
  sourceType: string,
  receipts: boolean = false
): Promise<TransactionAttachmentsResponse> => {
  const url = `/auth/journal/${tokenId}/attachments/${transactionId}/${transactionUuid}?sourceType=${sourceType}&receipts=${receipts}`;
  return api.get<TransactionAttachmentsResponse>(url, {
    headers: { [TOKEN_ID_HEADER]: tokenId },
  });
};

export const getTransactionAttachmentsByMetadata = (
  connectionId: string,
  metadataId: string
): Promise<TransactionAttachmentsResponse> => {
  const url = `/auth/journal/${connectionId}/attachments/metadata/${metadataId}`;
  return api.get<TransactionAttachmentsResponse>(url, {
    headers: { [TOKEN_ID_HEADER]: connectionId },
  });
};

export const uploadTransactionAttachment = (
  tokenId: string,
  transactionId: string,
  transactionUuid: string,
  file: File,
  sourceType: string
): Promise<TransactionAttachmentsResponse> => {
  const url = `/auth/journal/${tokenId}/attachments/${transactionId}/${transactionUuid}?sourceType=${sourceType}`;

  const formData = new FormData();
  formData.append('file', file);

  return api.post<TransactionAttachmentsResponse>(url, {
    body: formData,
    formData: true,
    headers: {
      [TOKEN_ID_HEADER]: tokenId,
    },
  });
};

export const exportToCsvRequest = (
  reportId: string,
  transactionType: JournalType,
  groupingType: string
): Promise<Response> => {
  const url = `/auth/vat/csv/${reportId}?transactionType=${transactionType}&groupingType=${groupingType}`;
  return api.get<Response>(url, { returnRawResponse: true });
};

export const exportToExcelRequest = (
  reportId: string,
  mappingId: string,
  boxValues: BoxValue[]
): Promise<Response> => {
  const url = `/auth/vat/excel/${reportId}/mapping/${mappingId}`;
  return api.post<Response>(url, {
    returnRawResponse: true,
    body: { boxValues },
  });
};

export const touchReport = (reportId: string): Promise<ApiResponse> => {
  const url = `/auth/vat/report/${reportId}/touch`;
  return api.post<ApiResponse>(url);
};

export const refreshReport = (
  data: VatGenerateRequestData,
  reportId: string
): Promise<GenerateReportResponse> => {
  const dataLogIdParam = data.dataLogId ? `&dataLogId=${data.dataLogId}` : '';
  const url =
    '/auth/vat/refresh/' +
    data.connectionId +
    '/mapping/' +
    data.mappingId +
    '?fromDate=' +
    data.fromDate +
    '&toDate=' +
    data.toDate +
    '&currency=' +
    data.currency +
    '&reportId=' +
    reportId +
    dataLogIdParam;
  return api.get<GenerateReportResponse>(url, {
    headers: {
      [TOKEN_ID_HEADER]: data.connectionId,
    },
  });
};